// Compact THEMIS runner for the Step orb's E row: the task's routine (a protocol's
// steps) with a countdown, Play / Next / Repeat — or, with no routine, a task timer.
// In-memory only; a finished routine logs a protocol_completions row like THEMIS does.
import { useCallback, useEffect, useRef, useState } from 'react';
import { writeRoutineRun } from './actions';
import type { ProtocolLite, RoutineStep } from './types';

export interface Runner {
  idx: number;
  n: number;
  /** Seconds left on the current step (or on the task timer). */
  left: number;
  running: boolean;
  finished: boolean;
  cur: RoutineStep | null;
  play: () => void;
  pause: () => void;
  next: () => void;
  repeat: () => void;
}

export const mmss = (s: number) => `${Math.floor(Math.max(0, s) / 60)}:${String(Math.max(0, s) % 60).padStart(2, '0')}`;

export function useRunner(userId: string | undefined, stepKey: string, routine: RoutineStep[] | null, protocol: ProtocolLite | null, fallbackMin: number): Runner {
  const steps = routine && routine.length ? routine : null;
  const n = steps ? steps.length : 0;
  const secsOf = useCallback((i: number) => Math.max(1, Math.round((steps ? steps[i]?.minutes ?? 1 : fallbackMin) * 60)), [steps, fallbackMin]);
  const [idx, setIdx] = useState(0);
  const [left, setLeft] = useState(() => secsOf(0));
  const [running, setRunning] = useState(false);
  const [finished, setFinished] = useState(false);
  const deadline = useRef(0);
  const startedAt = useRef<number | null>(null);

  const sig = `${stepKey}|${protocol?.id ?? ''}|${n}`;
  useEffect(() => {
    setIdx(0);
    setLeft(secsOf(0));
    setRunning(false);
    setFinished(false);
    startedAt.current = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig]);

  useEffect(() => {
    if (!running) return;
    const id = setInterval(() => {
      const l = Math.max(0, Math.round((deadline.current - Date.now()) / 1000));
      setLeft(l);
      if (l <= 0) setRunning(false);
    }, 500);
    return () => clearInterval(id);
  }, [running]);

  const play = useCallback(() => {
    if (finished) return;
    const l = left <= 0 ? secsOf(idx) : left;
    setLeft(l);
    deadline.current = Date.now() + l * 1000;
    if (startedAt.current == null) startedAt.current = Date.now();
    setRunning(true);
  }, [finished, left, idx, secsOf]);
  const pause = useCallback(() => setRunning(false), []);
  const repeat = useCallback(() => {
    const l = secsOf(idx);
    setLeft(l);
    deadline.current = Date.now() + l * 1000;
    if (startedAt.current == null) startedAt.current = Date.now();
    setRunning(true);
  }, [idx, secsOf]);
  const next = useCallback(() => {
    if (!steps) return;
    if (idx + 1 < n) {
      const l = secsOf(idx + 1);
      setIdx(idx + 1);
      setLeft(l);
      deadline.current = Date.now() + l * 1000;
      if (startedAt.current == null) startedAt.current = Date.now();
      setRunning(true);
      return;
    }
    setRunning(false);
    setFinished(true);
    setLeft(0);
    if (userId && protocol) void writeRoutineRun(userId, protocol.id, protocol.title, startedAt.current ?? Date.now(), n);
  }, [steps, idx, n, secsOf, userId, protocol]);

  return { idx, n, left, running, finished, cur: steps ? steps[Math.min(idx, n - 1)] ?? null : null, play, pause, next, repeat };
}
